import {Badge, TableRow} from 'platform/components';
import {HStack, Show, Text, ThemeColorPath} from 'platform/foundation';
import {CurrencyFormatter, NumberFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {ReactNode} from 'react';

import {always} from 'ramda';

import {SalesLeagueRow} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {SalesLeagueCell} from '../../../components/SalesLeagueCell';
import {SalesLeagueRank} from '../../../components/SalesLeagueRank';
import {SalesLeagueRankChange} from '../../../components/SalesLeagueRankChange';
import {SalesLeagueVehiclesChange} from '../../../components/SalesLeagueVehiclesChange';
import {formatMarginAmount} from '../../../utils/formatMarginAmount';
import {getSalesLeagueRepName} from '../utils/getSalesLeagueRepName';
import {SalesLeagueAvatar} from './SalesLeagueAvatar';

/** The podium is emphasised alongside the viewer's own row, matching the design. */
const PODIUM_LAST_RANK = 3;

const CURRENT_USER_ROW_COLOR: ThemeColorPath = 'palettes.blue.10.100';

interface SalesLeagueTableRowProps extends RequiredTestIdProps {
  row: SalesLeagueRow;
  currency: string;
  marginVisible: boolean;
  formatCurrency: CurrencyFormatter;
  formatNumber: NumberFormatter;
}

/**
 * `isCurrentUser` is a highlight, never a promise: only sellers reach the board, so a
 * manager — or a rep on a blank week — has no row of their own here. Nothing is
 * highlighted then, and the board stays complete.
 */
export function SalesLeagueTableRow(props: SalesLeagueTableRowProps) {
  const repName = getSalesLeagueRepName(props.row);
  const isEmphasised = props.row.rank <= PODIUM_LAST_RANK || props.row.isCurrentUser;

  const rowColor = match<boolean, ThemeColorPath | undefined>(props.row.isCurrentUser)
    .with(true, always(CURRENT_USER_ROW_COLOR))
    .otherwise(always(undefined));

  // Cells are assembled as a keyed array rather than with <Show>: TableRow maps its
  // children into <td> elements, so a <Show> that renders null would still occupy a
  // cell and desynchronise the row from the header.
  const cells: ReactNode[] = [
    <SalesLeagueCell key="rank">
      <SalesLeagueRank rank={props.row.rank} data-testid={suffixTestId('rank', props)} />
    </SalesLeagueCell>,
    <SalesLeagueCell key="rep">
      <HStack spacing={2} align="center">
        <SalesLeagueAvatar
          name={repName}
          userId={props.row.userId}
          data-testid={suffixTestId('rep', props)}
        />
        <Text size="small" alternative={isEmphasised} noWrap>
          {repName}
        </Text>
        <Show when={props.row.isCurrentUser}>
          <Badge
            value={i18n.t('page.salesLeague.table.currentUser')}
            colorScheme="blue"
            size="small"
            data-testid={suffixTestId('currentUser', props)}
          />
        </Show>
      </HStack>
    </SalesLeagueCell>,
    <SalesLeagueCell key="rankChange">
      <SalesLeagueRankChange
        trend={props.row.trend}
        data-testid={suffixTestId('rankChange', props)}
      />
    </SalesLeagueCell>,
    <SalesLeagueCell key="vehiclesChange">
      <SalesLeagueVehiclesChange
        vehiclesSoldChange={props.row.vehiclesSoldChange}
        formatNumber={props.formatNumber}
        data-testid={suffixTestId('vehiclesChange', props)}
      />
    </SalesLeagueCell>,
    <SalesLeagueCell key="vehiclesSold">
      <Text size="small" align="right" alternative>
        {props.formatNumber(props.row.vehiclesSold) ?? EMPTY_PLACEHOLDER}
      </Text>
    </SalesLeagueCell>,
    ...(props.marginVisible
      ? [
          <SalesLeagueCell key="marginPerVehicle">
            <Text size="small" align="right">
              {formatMarginAmount(props.row.marginPerVehicle, props.currency, props.formatCurrency)}
            </Text>
          </SalesLeagueCell>,
          <SalesLeagueCell key="totalMargin">
            <Text size="small" align="right" alternative>
              {formatMarginAmount(props.row.totalMargin, props.currency, props.formatCurrency)}
            </Text>
          </SalesLeagueCell>,
        ]
      : []),
  ];

  return (
    <TableRow color={rowColor} data-testid={suffixTestId('row', props)}>
      {cells}
    </TableRow>
  );
}
